import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveQuizBeforeTrtProfile } from "./quiz-before-trt-profile";
import { MM_0102 } from "./_experiments";

/**
 * BEFORE-TRT QUIZ -- cold-traffic destination for Ad 2 (the "considering TRT"
 * creative: "you're on injections for the rest of your life... is there
 * something I can try?"). Audience: men considering TRT, needle-nervous, not
 * on it yet.
 *
 * Spine: "your testosterone isn't broken -- it's blocked" -> the three thieves
 * (SHBG locks / aromatase converts to estrogen / cortisol tanks production) ->
 * try the one natural lever before the irreversible needle. The verdict is the
 * locked T-suppression surface with `blocked-testosterone` forced Primary (see
 * quizzes/quiz-before-trt-profile.ts); AROMATASE LOAD surfaces as organic Secondary
 * for soft/overweight builds, so the grid names two of the three thieves.
 *
 * 9 single-choice screens + 3 interstitials (2 bridges + analyzing) = 12.
 * Design spec: docs/specs/2026-06-08-quiz-before-trt-quiz-design.md.
 *
 * Sibling of `quiz-trt-switch` (Angle A, men already on TRT). Like quiz-v6, no GLP-1
 * screener, so `glp1-user` is not reachable from the classifier.
 */

const QUESTION_ORDER = [
  "age",
  "trt-status",
  "results-want",
  "energy-3pm",
  "body-composition",
  "libido",
  "info-not-broken",
  "when-started",
  "brain-fog",
  "trt-hesitation",
  "info-blocked",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "trt-status",
    type: "single",
    prompt: "Where are you with TRT right now?",
    options: [
      { value: "told-low", label: "My doctor or labs flagged low testosterone" },
      { value: "considering", label: "I've been seriously considering it" },
      { value: "symptomatic", label: "Not yet, but I feel the symptoms" },
      { value: "researching", label: "Just starting to look into it" },
    ],
  },
  {
    id: "results-want",
    type: "single",
    prompt: "If your testosterone were firing the way it should, what would you want back first?",
    options: [
      { value: "energy", label: "Energy that lasts all day" },
      { value: "muscle", label: "Muscle and strength" },
      { value: "sex-drive", label: "Drive and performance" },
      { value: "focus", label: "Mental clarity and focus" },
      { value: "all-of-above", label: "All of it" },
    ],
  },
  {
    id: "energy-3pm",
    type: "single",
    prompt: "How’s your energy at 3pm compared to when you wake up?",
    options: [
      { value: "drained", label: "Completely drained, need caffeine or a nap" },
      { value: "afternoon-slump", label: "Noticeably lower, afternoon slump hits hard" },
      { value: "slightly-tired", label: "Slightly tired, still functional but slower" },
      { value: "same-energy", label: "Same energy, no afternoon crash" },
    ],
  },
  {
    id: "body-composition",
    type: "single",
    prompt: "How would you describe your build right now?",
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Soft with some muscle" },
      { value: "overweight", label: "Overweight / out of shape" },
    ],
  },
  {
    id: "libido",
    type: "single",
    prompt: "Has your drive dropped compared to a few years ago?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone drives libido directly. When testosterone is being blocked, drive is usually the first thing to fade.",
    },
  },
  {
    id: "info-not-broken",
    type: "info",
    heading: "Your testosterone isn’t broken.",
    body: [
      "You work out, eat clean, and maybe your bloodwork came back “normal.” But you still feel it slipping.",
      "That’s not weakness, and it’s not just age. Modern life puts your body under a constant load it was never built for, the kind that quietly overrides effort.",
      "Something is getting in the way. A few more questions and we’ll show you exactly what.",
    ],
    // BRIDGE 1 image (operator-supplied): the "effort vs normal range" comic --
    // a man who eats clean, checks the mirror, and trains hard while a lab report
    // stamped Normal Range burns. Mirrors the absolution copy (you do everything
    // right, bloodwork says normal, you still feel it). Renders below the
    // Continue CTA (rule 20).
    image: {
      src: "/static/images/quiz-before-trt-not-broken.webp",
      alt: "A man eating a healthy meal, checking the mirror, and training hard while a lab report stamped Normal Range goes up in flames",
    },
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did you first notice these changes?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "brain-fog",
    type: "single",
    prompt: "How often does brain fog hit: losing focus mid-task, walking into a room and forgetting why?",
    options: [
      { value: "daily", label: "Daily" },
      { value: "most-days", label: "Most days" },
      { value: "sometimes", label: "Sometimes" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "trt-hesitation",
    type: "single",
    prompt: "When you picture being on TRT for the rest of your life, how does that sit with you?",
    options: [
      { value: "at-peace", label: "Fine, I’ve made peace with it" },
      { value: "needles", label: "Uneasy about the needles" },
      { value: "dependency", label: "Worried about the dependency" },
      {
        value: "shutdown-fertility",
        label: "I’m concerned about shutting down my natural production and/or I am considering having a child",
      },
      { value: "cost", label: "$400/mo for the rest of my life sounds insane" },
    ],
  },
  {
    id: "info-blocked",
    type: "info",
    heading: "Your testosterone isn’t gone. It’s blocked.",
    body: [
      "Your body is still making testosterone. Three thieves steal it before you can use it: SHBG locks it up. Excess estrogen-conversion drains what’s left. Cortisol shuts the signal down at the source.",
      "TRT floods the tank, but it never fixes the leak. And the moment you start, your body stops making its own. That part doesn’t always come back.",
      "Let’s see how blocked yours is.",
    ],
    // BRIDGE 2 image (operator-supplied): the "three thieves" T-suppression
    // mechanism diagram (SHBG locks T, estrogen conversion drains it, less free
    // T) -- byte-identical to the cortisol info-mechanism diagram, kept as a
    // per-quiz copy. The operator chose this mechanism visual over the
    // originally-planned first-product shot. Renders below the CTA (rule 20).
    image: {
      src: "/static/images/quiz-before-trt-blocked.webp",
      alt: "How testosterone gets blocked: the brain signals the testes, but SHBG locks up testosterone and estrogen conversion drains it, leaving less free testosterone in the body",
    },
  },
  {
    id: "info-analyzing",
    type: "info",
    heading: "Analyzing your testosterone profile…",
    body: [
      "Measuring how much of your testosterone is blocked, locked up, converted, or shut down before it can reach your body.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizBeforeTrtQuiz = defineQuiz({
  id: "quiz-before-trt",
  slug: "quiz-before-trt",
  // Deliberately NOT enrolled in MM-0093, and the reason was specific to that
  // experiment: it promoted each quiz's ANGLE question above `age`, and this
  // quiz has no clean candidate -- its second screen is `trt-status`, a
  // journey screener. MM-0093 concluded with the promoted-angle arm losing,
  // so if anyone later wants to open this quiz on a symptom question instead
  // of `trt-status`, that is a fresh hypothesis needing its own test, not a
  // resumption of MM-0093. MM-0102 hides an intro block and needs no such
  // candidate, so this quiz is in.
  experiment: MM_0102,
  meta: {
    title: "Is Your Testosterone Blocked?",
    introHeadline: "Is your testosterone REALLY broken, or just blocked?",
    introBody:
      "A 2-minute assessment to see exactly what’s blocking yours before you commit to the needle.",
    testimonial: {
      // Quiz-benefit entry proof: praises taking the quiz, not the product, so
      // it satisfies rule 19 (no angle/benefit leak before the villain reveal).
      // Invented per operator; back the Verified tag with a real "almost went on
      // TRT / tried this first" review before relying on it.
      quote:
        "I was a week from booking a TRT clinic. Taking this quiz first was the best two minutes I’ve spent. Cleared a lot up before I committed to anything.",
      author: "Ryan T., Verified Review",
      avatar: "/static/images/quiz-before-trt-testimonial-avatar.webp",
    },
    // MM-0102 -- see quizzes/_experiments.ts.
    introVariantTag: "control",
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const status = answers["trt-status"];
    const age = answers["age"];
    const body = answers["body-composition"];
    const energy = answers["energy-3pm"];
    const onset = answers["when-started"];
    const libido = answers["libido"];
    const want = answers["results-want"];

    // Performance-focused: lean body chasing strength / a cognitive edge.
    if (body === "lean-muscular" && (want === "muscle" || want === "focus")) {
      return "performance";
    }

    // TRT-alternative: told low T, or a long-running collapse across energy + drive.
    if (
      status === "told-low" ||
      (onset === "over-year" && energy === "drained" && (libido === "significantly" || age === "50-plus"))
    ) {
      return "trt-alternative";
    }

    // Skeptical buyer: just researching, or can't place when it started.
    if (status === "researching" || onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ with visible softening.
    if ((age === "50-plus" || age === "40-49") && (body === "soft-some-muscle" || body === "overweight")) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveQuizBeforeTrtProfile,
});
